import { DecimalPipe, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    inject
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { clone, groupBy, isNil } from "lodash";
import { Observable, Subject, forkJoin } from "rxjs";
import { catchError, map, take, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { CollectorService } from "~app/common/components/map/components/menu/map-collector/collector.service";
import { IMapGeometry } from "~app/common/components/map/map.settings";
import { SpatialService } from "~app/common/components/map/services/spatial.service";
import { Subtyp1, Subtyp2 } from "~app/common/components/map/symbols/kartgeometri.constants";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import {
    CollectorImportKalla,
    ConfirmDialogResponse,
    DrivningspunktTyper,
    Planeringsstatus,
    VirkeskopAffarsformer
} from "~app/common/models/constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { GeometrierDuplicateDTO } from "~app/common/models/geometri/geometri";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IKartgeometri } from "~app/features/map/popups/kartgeometri-popup/kartgeometri.model";
import { Kartgeometrityper } from "~app/features/planera/services/karta.service";
import { ValidationMessageRowComponent } from "../../../../common/validation/validation-message-row/validation-message-row.component";
import {
    ICollectorAtgardViewModel,
    ICollectorDrivningslinje,
    ICollectorDrivningspunkt,
    ICollectorDrivningspunktViewModel,
    ICollectorGeometrier,
    ICollectorHansynslinje,
    ICollectorHansynspunkt,
    ICollectorHansynsyta,
    ICollectorSubtyper,
    ICollectorgeometri
} from "../../models/atgard-collector-models";
import { Atgard } from "../../models/atgard-models";
import { Geometri, ICollectorGeometriWrapper, ICollectorImported } from "../../models/karta-models";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";

@Component({
    selector: "rask-vk-karta-collector-popup",
    templateUrl: "./vk-karta-collector-popup.component.html",
    styleUrls: ["./vk-karta-collector-popup.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        SelectorComponent,
        FormsModule,
        ValidationMessageRowComponent,
        InputComponent,
        ButtonComponent,
        DecimalPipe,
        TranslatePipe
    ]
})
export class VkKartaCollectorPopupComponent implements OnInit, OnChanges, OnDestroy {
    atgarder: Atgard[] = [];
    atgarderFromCollector: ICollectorAtgardViewModel[] = [];
    @Output() cancelled = new EventEmitter<any>();
    collector: ICollectorGeometrier;
    drivningslinjerFromCollector: ICollectorDrivningslinje[] = [];
    drivningspunkterFromCollector: ICollectorDrivningspunkt[] = [];
    hansynslinjerFromCollector: ICollectorHansynslinje[] = [];
    hansynspunkterFromCollector: ICollectorHansynspunkt[] = [];
    hansynsytorFromCollector: ICollectorHansynsyta[] = [];
    isActive: boolean;
    @Input() kartgeometrier: Geometri[];
    listHoverId: any; // Används för att skicka id till list-component för vilken rad den ska highlighta när man hovrar över en åtgärd i kartan

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onImported = new EventEmitter<any>();
    @Input() searchGeometry: string;
    @Input() useSpringfieldLayer: boolean;

    protected importInvalid = false;

    private readonly busyService = inject(BusyService);
    private readonly collectorService = inject(CollectorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly raskModalService = inject(RaskModalService);
    private readonly spatialService = inject(SpatialService);
    private readonly translateService = inject(TranslateService);
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);

    private _kontrakt: EntityDTO;

    get kontrakt(): EntityDTO {
        return this._kontrakt;
    }

    @Input()
    set kontrakt(value: EntityDTO) {
        this._kontrakt = value;
    }

    cancel() {
        this.cancelled.emit();
    }

    convert(typ: string, coll: ICollectorgeometri): ICollectorGeometriWrapper {
        return {
            typ: typ,
            geometri: coll as any as ICollectorSubtyper,
            selected: true,
            existsInKontrakt: false
        };
    }

    hasAtgardsYtor() {
        return this.collector?.geometrier?.atgarder?.length > 0;
    }

    hasErrors(): boolean {
        return this.importInvalid;
    }

    hasGeometries() {
        return this.hasAtgardsYtor() || this.hasOvrigaGeometrier();
    }

    hasMultipleLarmpunktForAtgard(): boolean {
        const koppladeDrivningspunkter = this.collector?.geometrier?.drivningspunkter.filter(x => ObjectUtils.hasValue(x.selectedAtgard));
        const groupBySubTyp2 = groupBy(koppladeDrivningspunkter, (x) => x.subtyp2);
        this.importInvalid = groupBySubTyp2?.Larmpunkt?.length > 1;
        return this.importInvalid;
    }

    hasOvrigaGeometrier() {
        return this.collector?.geometrier?.drivningslinjer?.length > 0 ||
            this.collector?.geometrier?.drivningspunkter?.length > 0 ||
            this.collector?.geometrier?.hansynslinjer?.length > 0 ||
            this.collector?.geometrier?.hansynspunkter?.length > 0 ||
            this.collector?.geometrier?.hansynsytor?.length > 0;
    }

    highlight(row: ICollectorAtgardViewModel) {
        return row.guid === this.listHoverId;
    }

    highlightDrivningspunkt(row: ICollectorDrivningspunktViewModel) {
        return row.guid === this.listHoverId;
    }

    import() {
        const buildResult = (kontraktGeometrier, updatedGeometrier, atgardgeometrier, drivningspunktgeometrier, cont) => ({
            kontraktgeometrier: kontraktGeometrier,
            atgardgeometrier: atgardgeometrier,
            drivningspunktgeometrier: drivningspunktgeometrier,
            updatedgeometrier: updatedGeometrier,
            continuecollector: cont
        });

        if (!this.kontraktHasAtgarder()) {
            const settings = new ModalSettings(this.translateService.instant("VIRKESKOP_ANBUD_KONTRAKT_COLLECTOR_DIALOG_TITLE"),
                this.translateService.instant("VIRKESKOP_ANBUD_KONTRAKT_COLLECTOR_DIALOG_MESSAGE"));
            settings.yesCaption = this.translateService.instant("VIRKESKOP_ANBUD_KONTRAKT_COLLECTOR_DIALOG_IMPORT");
            settings.noCaption = this.translateService.instant("VIRKESKOP_ANBUD_KONTRAKT_COLLECTOR_DIALOG_CANCEL");
            settings.contentIsMarkup = true;
            this.raskModalService.confirm(settings)
                .subscribe({
                    next: (modal) => {
                        if (modal === ConfirmDialogResponse.save) {
                            this.importCollectorGeometrierOvrigaNoDuplicates$().subscribe((response) => {
                                this.onImported.emit(buildResult(
                                    response.kontraktgeometrier,
                                    response.updatedgeometrier,
                                    response.atgardgeometrier,
                                    response.drivningspunktgeometrier,
                                    false));
                            });
                        } else {
                            this.onImported.emit(null);
                        }
                    },
                    error: () => {
                        this.onImported.emit(null);
                    }
                });
        } else {
            let fieldmapsAtgarder: ICollectorAtgardViewModel[];
            if (this.useSpringfieldLayer) {
                fieldmapsAtgarder = this.collector.geometrier.atgarder.filter(x => !isNil(x.selectedAtgard) && x.importkalla === CollectorImportKalla.Springfield);
            } else {
                fieldmapsAtgarder = this.collector.geometrier.atgarder.filter(x => !isNil(x.selectedAtgard) && x.importkalla === CollectorImportKalla.Fieldmaps);
            }

            const findDuplicatesFunc = arr => arr.filter((item, index) => arr.indexOf(item) !== index);

            const atgardGuids = fieldmapsAtgarder.map(x => x.selectedAtgard.guid);
            const moreThanOne = findDuplicatesFunc(atgardGuids);

            if (moreThanOne && moreThanOne.length > 0) {
                this.raskModalService.confirm(new ModalSettings("Import av flera åtgärdsytor till en åtgärd",
                    "Om du slår ihop flera Gysingeåtgärder till en Raskåtgärd kan trädslagsdata inte importeras till Rask med automatik. Vill du fortsätta?"))
                    .subscribe({
                        next: modal => {
                            if (modal === ConfirmDialogResponse.save) {
                                this.importCollectorGeometrierOvrigaNoDuplicates(buildResult);
                            } else {
                                return;
                            }
                        },
                        error: () => {
                            //
                        }
                    });
            } else {
                this.importCollectorGeometrierOvrigaNoDuplicates(buildResult);
            }
        }
    }

    importCollectorGeometrierOvrigaNoDuplicates(buildResult: (
        kontraktGeometrier: any,
        updatedGeometrier: any,
        atgardgeometrier: any,
        drivningspunktgeometrier: any,
        cont: any) => ICollectorImported) {
        this.importCollectorGeometrierOvrigaNoDuplicates$().subscribe((response) => {
            const atgarder = this.importCollectorAtgarder();
            const drivningspunkter = this.importCollectorDrivningspunkter();
            const settings = new ModalSettings(this.translateService.instant("VIRKESKOP_ANBUD_KONTRAKT_COLLECTOR_DIALOG_END_TITLE"),
                this.translateService.instant("VIRKESKOP_ANBUD_KONTRAKT_COLLECTOR_DIALOG_END_MESSAGE"));
            settings.yesCaption = this.translateService.instant("DEFAULT_BUTTON_LABEL_OK");
            settings.noCaption = this.translateService.instant("VIRKESKOP_ANBUD_KONTRAKT_COLLECTOR_DIALOG_END_CANCEL");
            this.raskModalService.confirm(settings)
                .subscribe({
                    next: (res) => {
                        if (res === ConfirmDialogResponse.save) {
                            this.onImported.emit(buildResult(response.kontraktgeometrier, response.updatedgeometrier, atgarder, drivningspunkter, false));
                        } else {
                            this.onImported.emit(buildResult(response.kontraktgeometrier, response.updatedgeometrier, atgarder, drivningspunkter, true));
                        }
                    },
                    error: () => {
                        this.onImported.emit(buildResult(response.kontraktgeometrier, response.updatedgeometrier, atgarder, drivningspunkter, true));
                    }
                });
        });
    }

    isLeveransvirke(): boolean {
        return this.kontraktMediatorService.kontrakt?.affarsform === VirkeskopAffarsformer.Leveransvirke;
    }

    itemDisabled = (item: Atgard) => item.status !== Planeringsstatus.UnderPlanering;

    kontraktHasAtgarder() {
        return this.atgarder?.length > 0;
    }

    readonly listMouseHover = (row: any, hover: boolean) => {
        //
    };

    markDuplicates(collectorKartgeometrier: ICollectorGeometriWrapper[]): ICollectorGeometriWrapper[] {
        const guids = this.kartgeometrier.map(x => x.guid);
        collectorKartgeometrier.forEach(x => {
            const alreadyImported = guids.some(guid => guid === (x.geometri as any as ICollectorgeometri).guid);
            x.existsInKontrakt = alreadyImported;
            x.selected = !alreadyImported;
        });
        return collectorKartgeometrier;
    }

    mergeAndWrapAllKontraktGeometrier(): ICollectorGeometriWrapper[] {
        const x1 = this.collector.geometrier.drivningslinjer.map(x => this.convert(Kartgeometrityper.Drivningslinje, x));
        const x2 = this.collector.geometrier.drivningspunkter.map(x => this.convert(Kartgeometrityper.Drivningspunkt, x));
        const x3 = this.collector.geometrier.hansynslinjer.map(x => this.convert(Kartgeometrityper.HansynLinje, x));
        const x4 = this.collector.geometrier.hansynspunkter.map(x => this.convert(Kartgeometrityper.HansynPunkt, x));
        const x5 = this.collector.geometrier.hansynsytor.map(x => this.convert(Kartgeometrityper.HansynYta, x));
        const x6 = this.collector.geometrier.hansynsytor.map(x => this.convert(Kartgeometrityper.AnvisningYta, x));
        return x1.concat(x2).concat(x3).concat(x4).concat(x5).concat(x6);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!ObjectUtils.isNullOrUndefined(changes.searchGeometry?.currentValue)) {
            this.getCollectorGeometries(this.searchGeometry).pipe(
                catchError(() => []),
                take(1)
            ).subscribe();
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.atgarderFromCollector = [];
        this.searchGeometry = null;
        this.isActive = false;

        this.kontraktMediatorService.atgarder$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(atgarder => {
            this.atgarder = atgarder;
        });
    }

    outputFormatter = (item: Atgard) => item;

    viewFormatter = (item: Atgard) =>
        (`${(ObjectUtils.hasValue(item?.beteckning) ? item.beteckning : "")} ${ObjectUtils.hasValue(item.status) ? item.status : ""}`);

    private convertToKartgeometri(collector: ICollectorSubtyper[]) {
        return collector.map((c: any) => ({
            guid: c.guid,
            geometri: c.geometry?.wkt,
            subtyp1: c.subtyp1,
            subtyp2: c.subtyp2,
            subtyp3: (c.subtyp1 === Subtyp1.drivningspunkt && c.subtyp2 === Subtyp2.avlagg) ? undefined : c.subtyp3
        } as IKartgeometri));
    }

    private getCollectorGeometries(arcGeometry: string): Observable<ICollectorGeometrier> {
        const collectorWork = forkJoin([
            this.collectorService.importAtgarderFromCollector({
                searchGeometry: arcGeometry,
                importFromSpringfield: this.useSpringfieldLayer
            }),
            this.collectorService.importDrivningslinjerFromCollector({
                searchGeometry: arcGeometry,
                importFromSpringfield: this.useSpringfieldLayer
            }),
            this.collectorService.importDrivningspunkterFromCollector({
                searchGeometry: arcGeometry,
                importFromSpringfield: this.useSpringfieldLayer
            }),
            this.collectorService.importHansynslinjerFromCollector({
                searchGeometry: arcGeometry,
                importFromSpringfield: this.useSpringfieldLayer
            }),
            this.collectorService.importHansynspunkterFromCollector({
                searchGeometry: arcGeometry,
                importFromSpringfield: this.useSpringfieldLayer
            }),
            this.collectorService.importHansynsytorFromCollector({
                searchGeometry: arcGeometry,
                importFromSpringfield: this.useSpringfieldLayer
            })
        ]);
        return this.busyService.addWork(collectorWork)
            .pipe(map(result => {
                this.collector = {
                    geometrier: {
                        atgarder: result[0].atgarder as ICollectorAtgardViewModel[],
                        drivningslinjer: result[1].drivningslinjer,
                        drivningspunkter: result[2].drivningspunkter as ICollectorDrivningspunktViewModel[],
                        hansynslinjer: result[3].hansynslinjer,
                        hansynspunkter: result[4].hansynspunkter,
                        hansynsytor: result[5].hansynsytor
                    }
                };
                if (this.isLeveransvirke()) {
                    this.collector.geometrier.drivningspunkter =
                        this.collector.geometrier.drivningspunkter.filter(x => x.subtyp2 === DrivningspunktTyper.larmpunkt || x.subtyp2 === DrivningspunktTyper.avlagg);
                }
                return this.collector;
            }));
    }

    private importCollectorAtgarder(): ICollectorAtgardViewModel[] {
        const result: ICollectorAtgardViewModel[] = [];
        this.atgarder?.forEach(atg => {
            const nyaCollectorAtgarder = this.collector.geometrier.atgarder.filter(ca => ca.selectedAtgard?.guid === atg.guid);
            if (nyaCollectorAtgarder?.length > 0) {
                const selectedAtgard = nyaCollectorAtgarder[0].selectedAtgard;
                const nyaCollectorGeometries = nyaCollectorAtgarder?.map(a => ({
                    wkt: a.geometry.wkt,
                    id: a.guid
                } as IMapGeometry));
                const joinedGeometry = this.spatialService.join(nyaCollectorGeometries);
                const area = this.spatialService.getAreaWkt(joinedGeometry.wkt);
                /* eslint-disable sonarjs/avoid-mutating-nested-properties-of-shallow-clones */
                const nyJoinedCollectorAtgard = clone(nyaCollectorAtgarder[0]);
                nyJoinedCollectorAtgard.geometry.wkt = joinedGeometry.wkt;
                nyJoinedCollectorAtgard.geometry.area = area;
                nyJoinedCollectorAtgard.selectedAtgard = selectedAtgard;
                nyJoinedCollectorAtgard.anteckning = nyaCollectorAtgarder?.map(a => a.anteckning ?? "").join(", ");
                nyJoinedCollectorAtgard.selectedAtgard.geometri = joinedGeometry.wkt;
                /* eslint-enable sonarjs/avoid-mutating-nested-properties-of-shallow-clones */
                result.push(nyJoinedCollectorAtgard);
            }
        });
        return result;
    }

    private importCollectorDrivningspunkter(): ICollectorDrivningspunktViewModel[] {
        return this.collector.geometrier.drivningspunkter.filter(x => ObjectUtils.hasValue(x.selectedAtgard));
    }

    private importCollectorGeometrierOvrigaNoDuplicates$(): Observable<ICollectorImported> {
        const done = new Subject<ICollectorImported>();
        const importing = (this.collector.geometrier.drivningslinjer as ICollectorSubtyper[])
            .concat(this.collector.geometrier.drivningspunkter)
            .concat(this.collector.geometrier.hansynslinjer)
            .concat(this.collector.geometrier.hansynspunkter)
            .concat(this.collector.geometrier.hansynsytor);

        const kontraktGeometrier = this.vkKartaMediatorService.kontraktGeometrier$.value;
        const kontraktKartGeometrier = kontraktGeometrier.map(x => x.kartgeometri);

        this.collectorService.compare(this.convertToKartgeometri(importing), kontraktKartGeometrier).subscribe({
            next: duplicates => {
                const importUpdated = this.importTextsOnly(importing, duplicates.filter(x => x.hasDuplicatesInDestination === true));
                const importingNewGuids = duplicates.filter(x => x.hasDuplicatesInDestination === false).map(x => x.objects[0].guid);
                const importNew = importing.filter(x => importingNewGuids.some(g => g === (x as any as ICollectorgeometri).guid));
                const response = {
                    kontraktgeometrier: !this.isLeveransvirke() ? importNew.map((g) => ({
                        typ: g.subtyp1,
                        geometri: g,
                        selected: true,
                        existsInKontrakt: false
                    })) : [],
                    updatedgeometrier: importUpdated,
                    atgardgeometrier: [],
                    continuecollector: false
                } as ICollectorImported;
                done.next(response);
                done.complete();
            },
            error: (e) => { done.error(e); }
        });

        return done;
    }

    private importTextsOnly(importing: ICollectorSubtyper[], duplicates: GeometrierDuplicateDTO[]): ICollectorSubtyper[] {
        const updated = [];
        const existingGeometries = this.vkKartaMediatorService.kontraktGeometrier$.value;

        const overWriteTextFromSource = (fromGuid: string, toGuid: string) => {
            const fromGeometry = importing.find(g => (g as any).guid === fromGuid) as any;
            const toGeometry = existingGeometries.find(x => x.guid === toGuid);
            if (!isNil(fromGeometry) && !isNil(toGeometry)) {
                const changed = toGeometry.kartgeometri.anteckning !== fromGeometry.anteckning ||
                    toGeometry.kartgeometri.tillganglighetsklass !== fromGeometry.tillganglighetsklass ||
                    toGeometry.kartgeometri.karttext !== fromGeometry.karttext;
                toGeometry.kartgeometri.anteckning = fromGeometry.anteckning;
                toGeometry.kartgeometri.tillganglighetsklass = fromGeometry.tillganglighetsklass;
                toGeometry.kartgeometri.karttext = fromGeometry.karttext;
                if (changed && updated.some(g => g.guid === toGeometry.guid) === false) {
                    updated.push(toGeometry.kartgeometri);
                }
            }
        };

        duplicates.forEach(d => {
            const from = d.objects.filter(x => x.isSource === true);
            const to = d.objects.filter(x => x.isSource === false);
            if (ObjectUtils.hasValue(from) && from.length > 0 && ObjectUtils.hasValue(to) && to.length > 0) {
                overWriteTextFromSource(from[0].guid, to[0].guid);
            }
        });

        return updated;
    }
}
